import React, { useState } from 'react'
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Alert,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material'
import GeminiAssistant from './GeminiAssistant'

const STEPS = ['Tyyppi', 'Kategoria', 'Kuvaus', 'Lähetä']

const CATEGORIES = [
  { value: 'new_feature', label: 'Uusi ominaisuus', description: 'Täysin uusi toiminnallisuus järjestelmään' },
  { value: 'change_existing', label: 'Muutos olemassaolevaan', description: 'Parannusehdotus nykyiseen toimintoon' },
]

const SUBCATEGORIES = [
  { value: 'booking', label: 'Varaukset', description: 'Varausten hallinta ja kalenteri' },
  { value: 'payments', label: 'Maksut', description: 'Maksaminen, laskutus ja kassatoiminnot' },
  { value: 'reporting', label: 'Raportointi', description: 'Raportit ja tilastot' },
  { value: 'customers', label: 'Asiakkaat', description: 'Asiakastietojen hallinta' },
  { value: 'resources', label: 'Resurssit', description: 'Resurssien ja tilojen hallinta' },
  { value: 'activities', label: 'Aktiviteetit', description: 'Tapahtumat ja ryhmätunnit' },
  { value: 'communication', label: 'Viestintä', description: 'Sähköpostit ja ilmoitukset' },
  { value: 'settings', label: 'Asetukset', description: 'Järjestelmäasetukset' },
  { value: 'other', label: 'Muu', description: 'Jokin muu osa-alue' },
]

function FeatureRequestForm({ firebase, onSubmitSuccess, onCancel }) {
  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    title: '',
    description: '',
    originalText: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleNext = () => {
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value })
  }

  const handleSubcategoryChange = (value) => {
    setFormData({ ...formData, subcategory: value })
  }

  const handleAcceptSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      title: suggestion.title,
      description: suggestion.description,
    })
  }

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!formData.category
      case 1:
        return !!formData.subcategory
      case 2:
        return !!formData.title.trim() && !!formData.description.trim()
      default:
        return true
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)

    try {
      await firebase.submitFeatureRequest({
        category: formData.category,
        subcategory: formData.subcategory,
        title: formData.title,
        description: formData.description,
        originalText: formData.originalText || formData.description,
      })
      onSubmitSuccess()
    } catch (err) {
      console.error('Submit error:', err)
      setError('Toiveen lähettäminen epäonnistui. Yritä uudelleen.')
    } finally {
      setSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Millainen toive sinulla on?
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                {CATEGORIES.map((cat) => (
                  <Paper
                    key={cat.value}
                    elevation={formData.category === cat.value ? 3 : 1}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      border: formData.category === cat.value ? '2px solid' : 'none',
                      borderColor: 'primary.main',
                    }}
                    onClick={() => handleCategoryChange(cat.value)}
                  >
                    <FormControlLabel
                      value={cat.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">{cat.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {cat.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Mihin osa-alueeseen toive liittyy?
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={formData.subcategory}
                onChange={(e) => handleSubcategoryChange(e.target.value)}
              >
                <Grid container spacing={1}>
                  {SUBCATEGORIES.map((cat) => (
                    <Grid item xs={12} sm={6} key={cat.value}>
                      <Paper
                        elevation={formData.subcategory === cat.value ? 3 : 1}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          height: '100%',
                          border: formData.subcategory === cat.value ? '2px solid' : 'none',
                          borderColor: 'primary.main',
                        }}
                        onClick={() => handleSubcategoryChange(cat.value)}
                      >
                        <FormControlLabel
                          value={cat.value}
                          control={<Radio size="small" />}
                          label={
                            <Box>
                              <Typography variant="body1">{cat.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {cat.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </Box>
        )

      case 2:
        // Show accepted suggestion or chat
        if (formData.title && formData.description) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Hyväksytty ehdotus
              </Typography>
              <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Otsikko
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {formData.title}
                </Typography>

                <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Kuvaus
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {formData.description}
                </Typography>
              </Paper>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setFormData({ ...formData, title: '', description: '' })}
              >
                Muokkaa keskustelemalla uudelleen
              </Button>
            </Box>
          )
        }

        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Kuvaile toiveesi
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Keskustele AI-avustajan kanssa ja kerro mitä toiminnallisuutta kaipaat.
              Avustaja auttaa muotoilemaan toiveesi selkeäksi ehdotukseksi.
            </Typography>
            <GeminiAssistant
              firebase={firebase}
              onAcceptSuggestion={handleAcceptSuggestion}
              initialDescription={formData.description}
            />
          </Box>
        )

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Tarkista ja lähetä
            </Typography>
            <Paper elevation={1} sx={{ p: 3, mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Tyyppi
              </Typography>
              <Typography variant="body1" gutterBottom>
                {CATEGORIES.find((c) => c.value === formData.category)?.label}
              </Typography>

              <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Kategoria
              </Typography>
              <Typography variant="body1" gutterBottom>
                {SUBCATEGORIES.find((c) => c.value === formData.subcategory)?.label}
              </Typography>

              <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Otsikko
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.title}
              </Typography>

              <Typography variant="overline" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Kuvaus
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {formData.description}
              </Typography>
            </Paper>

            <Alert severity="info">
              Toiveesi lähetetään moderointiin. Hyväksytyt toiveet julkaistaan
              äänestettäväksi kaikkien hallien/paikkojen Admin ja Kassa käyttäjille.
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 300 }}>{renderStepContent()}</Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onCancel} color="inherit">
          Peruuta
        </Button>
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Takaisin
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={!canProceed()}>
              Seuraava
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || !canProceed()}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Lähetetään...' : 'Lähetä toive'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default FeatureRequestForm
